export type ItGlueAccessCheckRow = {
  permission: string;
  ok: boolean;
  status?: "success" | "failure" | "scope_only";
  reason: string;
};

export type ItGlueAccessFeedback = {
  /** True when every row in `permissionChecks` passed (strict). */
  allChecksPassed: boolean;
  /** True when the server accepted the test as “save OK” (e.g. Password Access-only failure still allows save). */
  connectionTestPassed: boolean;
  /** MUI Alert severity: full pass, save-OK with warnings, or hard failure. */
  alertSeverity: "success" | "warning" | "error";
  message: string;
  permissionChecks: ItGlueAccessCheckRow[];
  missingRequirements: string[];
};

export function itGlueAccessStatusLabel(check: ItGlueAccessCheckRow): "Success" | "Failure" | "Scope Only" {
  if (check.status === "scope_only") return "Scope Only";
  return check.ok ? "Success" : "Failure";
}

export function buildItGlueAccessFeedback(apiResult: {
  success?: boolean;
  details?: {
    permission_checks?: ItGlueAccessCheckRow[];
    missing_requirements?: string[];
  };
}): ItGlueAccessFeedback {
  const details = apiResult?.details || {};
  const permissionChecks = Array.isArray(details.permission_checks)
    ? details.permission_checks
        .filter((p) => p && typeof p.permission === "string")
        .map((p) => ({
          permission: String(p.permission),
          ok: Boolean(p.ok),
          status:
            p.status === "success" || p.status === "failure" || p.status === "scope_only"
              ? p.status
              : undefined,
          reason: String(p.reason || ""),
        }))
    : [];
  const failed = permissionChecks.filter((p) => !p.ok);
  const allChecksPassed = failed.length === 0;
  const connectionTestPassed = Boolean(apiResult?.success);
  const alertSeverity: ItGlueAccessFeedback["alertSeverity"] = allChecksPassed
    ? "success"
    : connectionTestPassed
      ? "warning"
      : "error";
  const missingRequirements = Array.isArray(details.missing_requirements)
    ? details.missing_requirements.map((x: unknown) => String(x))
    : [];
  const documentationAccessPartial =
    connectionTestPassed &&
    !allChecksPassed &&
    failed.length === 1 &&
    String(failed[0]?.permission || "") === "Documentation access";

  const message = allChecksPassed
    ? "All access checks passed."
    : documentationAccessPartial
      ? "Connected — enable Password Access on your IT Glue API key to finish documentation access. You can still save."
      : connectionTestPassed
        ? "Connected, but one or more checks need attention. You can save; see below."
        : `Access checks failed (${failed.length} of ${permissionChecks.length}).`;
  return {
    allChecksPassed,
    connectionTestPassed,
    alertSeverity,
    message,
    permissionChecks,
    missingRequirements,
  };
}
